import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { getPublicOrderSample } from '../data/publicDocumentSamples';

const formatCurrency = (amount: number, currencyCode: string) =>
  amount.toLocaleString('fr-FR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const OrderSummary = () => {
  const { orderNumber } = useParams();
  const order = getPublicOrderSample(orderNumber);

  return (
    <>
      <Helmet>
        <title>{order.orderNumber} - Suivi de commande | Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Consultez le récapitulatif de commande, la facture associée et les prochaines étapes de votre projet avec Couteau Suisse Digital."
        />
      </Helmet>

      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="space-y-8"
          >
            <section className="overflow-hidden rounded-3xl bg-white shadow-xl">
              <div className="border-b border-[#f1ece8] bg-gradient-to-b from-[#f7f3ef] to-[#fffaf7] px-8 py-10">
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-neon-orange">
                  Suivi de commande
                </div>
                <h1 className="mt-3 text-4xl font-bold text-gray-900">{order.orderNumber}</h1>
                <p className="mt-4 max-w-2xl text-lg text-gray-600">
                  Votre commande pour <strong>{order.serviceName}</strong> a bien été enregistrée.
                  Cette page centralise les informations utiles et les prochains points de contact.
                </p>
              </div>

              <div className="grid gap-8 px-8 py-8 lg:grid-cols-[minmax(0,1.25fr)_320px]">
                <div className="space-y-6">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                    <h2 className="mb-4 text-xl font-semibold text-gray-900">
                      Récapitulatif
                    </h2>
                    <div className="space-y-3 text-sm text-gray-700">
                      <div className="flex justify-between gap-4">
                        <span>Date de commande</span>
                        <strong className="text-right">{formatDate(order.orderDate)}</strong>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Client</span>
                        <strong className="text-right">{order.clientName}</strong>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Entreprise</span>
                        <strong className="text-right">{order.clientCompany}</strong>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Email</span>
                        <strong className="text-right">{order.clientEmail}</strong>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Service</span>
                        <strong className="text-right">{order.serviceName}</strong>
                      </div>
                      <div className="flex justify-between gap-4 text-base">
                        <span>Montant</span>
                        <strong className="text-right text-neon-orange">
                          {formatCurrency(order.amount, order.currencyCode)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-6">
                    <h2 className="mb-4 text-xl font-semibold text-gray-900">
                      Prochaines étapes
                    </h2>
                    <ul className="space-y-3 text-sm leading-6 text-gray-600">
                      <li>Notre équipe confirme la portée du projet et le planning initial.</li>
                      <li>Vous recevez les éventuels documents complémentaires et demandes d’accès.</li>
                      <li>Un point de cadrage peut être planifié pour valider les priorités.</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-5">
                  <div className="rounded-2xl border border-[#f6d8c0] bg-[#fff4eb] p-6">
                    <div className="text-sm font-semibold uppercase tracking-wide text-[#7c4a22]">
                      Documents liés
                    </div>
                    <div className="mt-4 space-y-3">
                      <Link
                        to={`/documents/facture/${order.invoiceNumber}`}
                        className="inline-flex w-full items-center justify-center rounded-full bg-neon-orange px-6 py-4 font-semibold text-white transition-colors hover:bg-[#FF4500]"
                      >
                        Voir la facture
                      </Link>
                      <Link
                        to={`/documents/devis/${order.quoteNumber}`}
                        className="inline-flex w-full items-center justify-center rounded-full border border-neon-orange px-6 py-4 font-semibold text-neon-orange transition-colors hover:bg-neon-orange hover:text-white"
                      >
                        Voir le devis
                      </Link>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-6">
                    <h3 className="mb-3 text-lg font-semibold text-gray-900">
                      Besoin d’un échange ?
                    </h3>
                    <p className="text-sm leading-6 text-gray-600">
                      Si vous souhaitez ajuster un document, poser une question ou planifier un
                      appel, notre équipe reste disponible.
                    </p>
                    <div className="mt-5 flex flex-col gap-3">
                      <Link
                        to="/contact"
                        className="font-semibold text-neon-orange transition-colors hover:text-[#FF4500]"
                      >
                        Contacter le support
                      </Link>
                      <Link
                        to="/reserver"
                        className="font-semibold text-gray-900 transition-colors hover:text-neon-orange"
                      >
                        Réserver un appel
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </motion.div>
        </div>
      </main>
    </>
  );
};

export default OrderSummary;
